import { useCallback, useEffect, useRef, useState } from 'react';

// ── [T-DRAGGABLE-BOTTOM-HANGING-FULLBAR-DROPLET-X] ───────────────────────────
// X-axis-only pointer drag for the floating bar's bottom-hanging collapse/expand
// droplet. Persistence is scoped to the transactions-topbar droplet ONLY.
//
// [T-DRAGGABLE-REV11-PHASE-A] The original R39 hook had a 300ms long-press
// gate that canceled on any movement > 8px. This made the horizontal drag
// impossible on touch devices because users can't hold 300ms without any
// movement. The fix is to remove the long-press gate entirely and rely on
// threshold-based arming: touch arms at 1px cumulative X, mouse at > 8px.
//
// [T-DRAGGABLE-REV7-DIAGNOSTIC] Phase 1 instrumentation only (no behavior
// change): lazy window.__fintrack_drag_trace__[] + console.debug for
// pointerdown / pointermove / pointerup / pointercancel events.

const DROPLET_X_KEY = 'fintrack:txn-topbar-droplet-x';
const DROPLET_SIZE = 44;
const EDGE_MARGIN = 8;
const MOVE_THRESHOLD_PX = 8;
const TOUCH_THRESHOLD_PX = 1;
const SNAP_THRESHOLD_PX = 6;
const TOUCH_JITTER_PX = 2;
const TOUCH_JITTER_Y_PX = 2;
const TOUCH_HOLD_GATE_MS = 250;
const TOUCH_HOLD_CANCEL_PX = 6;
const V_GUARD_MULTIPLIER = 4;
const V_GUARD_PRIMED_MULTIPLIER = 5;

function getMoveThreshold(pointerType: string | undefined): number {
  return pointerType === 'touch' || pointerType === 'pen' ? TOUCH_THRESHOLD_PX : MOVE_THRESHOLD_PX;
}

interface DragTraceEntry {
  event: 'pointerdown' | 'pointermove' | 'pointerup' | 'pointercancel';
  ts: number;
  pointerId?: number; clientX?: number; clientY?: number;
  'dx-before'?: number; 'dx-final'?: number; 'dx-after-clamp'?: number;
  ddx?: number; ddy?: number; dragOrigin?: { x: number; y: number } | null;
  'hasDraggedRef.current'?: boolean; isDragging?: boolean; isFixed?: boolean;
  isMobile?: boolean; viewport?: number; stored?: boolean; pointerType?: string;
  threshold?: number; 'click-suppressed'?: boolean; note?: string;
}

declare global {
  interface Window {
    __fintrack_drag_trace__?: DragTraceEntry[];
  }
}

function isMobilePointer(): boolean {
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) return true;
  try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
}

function pushDragTrace(entry: DragTraceEntry): void {
  if (typeof window === 'undefined') return;
  const bucket: DragTraceEntry[] = window.__fintrack_drag_trace__ ?? [];
  if (!window.__fintrack_drag_trace__) window.__fintrack_drag_trace__ = bucket;
  bucket.push(entry);
}

type DropletAnchor = 'right' | 'center';

export interface UseDraggableDropletXOptions {
  size?: number;
  horizontalMargin?: number;
  anchor?: DropletAnchor;
}

interface DropletBounds {
  min: number;
  max: number;
}

function getBoundsForViewport(
  viewportWidth: number,
  size: number,
  horizontalMargin: number,
  anchor: DropletAnchor,
): DropletBounds {
  const availableRange = Math.max(0, viewportWidth - size - horizontalMargin * 2);
  if (anchor === 'center') {
    const halfRange = availableRange / 2;
    return { min: -halfRange, max: halfRange };
  }
  return { min: -availableRange, max: 0 };
}

function clampToBounds(value: number, bounds: DropletBounds): number {
  return Math.max(bounds.min, Math.min(bounds.max, value));
}

// ── [T-DRAGGABLE-REV7] Module-level pointer ID tracking ──────────────────────
// Ensures only one pointer can be tracked at a time, regardless of which
// hook instance is active. Active pointer tracking used by document-level
// listeners so the gesture is captured even on touch devices where the
// sticky bar wrapper interferes with setPointerCapture.

let lastActivePointerId: number | null = null;
type DocHandler = (e: PointerEvent) => void;
let activeMoveHandler: DocHandler | null = null;
let activeUpHandler: DocHandler | null = null;
let activeCancelHandler: DocHandler | null = null;

function teardownDocumentHandlers() {
  if (activeMoveHandler) document.removeEventListener('pointermove', activeMoveHandler);
  if (activeUpHandler) document.removeEventListener('pointerup', activeUpHandler);
  if (activeCancelHandler) document.removeEventListener('pointercancel', activeCancelHandler);
  activeMoveHandler = null;
  activeUpHandler = null;
  activeCancelHandler = null;
  lastActivePointerId = null;
}

export function useDraggableDropletX({
  size = DROPLET_SIZE,
  horizontalMargin = EDGE_MARGIN,
  anchor = 'right',
}: UseDraggableDropletXOptions = {}) {
  const [dx, setDxState] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    try {
      const stored = window.localStorage.getItem(DROPLET_X_KEY);
      if (stored !== null) {
        const parsed = parseFloat(stored);
        if (Number.isFinite(parsed)) {
          return clampToBounds(
            parsed,
            getBoundsForViewport(window.innerWidth, size, horizontalMargin, anchor),
          );
        }
      }
    } catch { /* fall through */ }
    return 0;
  });
  const setDx = useCallback((v: number) => {
    setDxState(v);
    currentDxRef.current = v;
  }, []);

  const [isDragging, setIsDragging] = useState(false);
  const dragOriginRef = useRef<{ x: number; y: number } | null>(null);
  const startDxRef = useRef<number>(0);
  const lastMoveRef = useRef<{ x: number; y: number } | null>(null);
  const hasDraggedRef = useRef<boolean>(false);
  const dvPointerTypeRef = useRef<string>('');
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchGateArmedRef = useRef<boolean>(false);
  const touchIsTouchLikeRef = useRef<boolean>(false);
  const currentDxRef = useRef<number>(0);
  const [isFixedFallback, setIsFixedFallback] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth <= 374,
  );

  const getBounds = useCallback((): { min: number; max: number } => {
    if (typeof window === 'undefined') return { min: 0, max: 0 };
    return getBoundsForViewport(window.innerWidth, size, horizontalMargin, anchor);
  }, [anchor, horizontalMargin, size]);

  // ── Pointer event handlers [T-DRAGGABLE-REV7] ────────────────────────────
  // The hook installs its own document-level listeners at press time so the
  // gesture is captured even on touch devices where the parent sticky wrapper
  // interferes with setPointerCapture.

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      pushDragTrace({
        event: 'pointerdown', ts: Date.now(), pointerId: e.pointerId,
        clientX: e.clientX, clientY: e.clientY, 'dx-before': dx,
        dragOrigin: null,
        viewport: typeof window === 'undefined' ? 0 : window.innerWidth,
        isFixed: typeof window !== 'undefined' && window.innerWidth <= 374,
        isDragging: false, isMobile: isMobilePointer(),
        pointerType: e.pointerType, threshold: getMoveThreshold(e.pointerType),
        note: 'down',
      });
      // Suppress synthesis click on touch.
      if (e.pointerType === 'touch' || e.pointerType === 'pen') {
        try { e.preventDefault(); } catch { /* passive listener */ }
      }

      // [T-FLOATING-BAR-NAV-TOGGLE-HOLD-PY] Non-desktop (touch|pen) requires a
      // tap-and-hold before drag can arm. Mouse on desktop keeps immediate
      // threshold-based arming per R41. The gate is invisible: only the
      // internal `touchGateArmedRef` flips, so `isDragging` remains the single
      // source of truth for visual state.
      const isTouchLike = e.pointerType === 'touch' || e.pointerType === 'pen';
      touchIsTouchLikeRef.current = isTouchLike;
      if (isTouchLike) {
        touchGateArmedRef.current = false;
        if (longPressTimerRef.current !== null) {
          clearTimeout(longPressTimerRef.current);
        }
        longPressTimerRef.current = setTimeout(() => {
          touchGateArmedRef.current = true;
        }, TOUCH_HOLD_GATE_MS);
      } else {
        touchGateArmedRef.current = true;
        longPressTimerRef.current = null;
      }

      // If another pointer is already active, ignore this press.
      if (lastActivePointerId !== null && lastActivePointerId !== e.pointerId) return;
      lastActivePointerId = e.pointerId;
      dvPointerTypeRef.current = e.pointerType ?? '';

      dragOriginRef.current = { x: e.clientX, y: e.clientY };
      lastMoveRef.current = { x: e.clientX, y: e.clientY };
      startDxRef.current = dx;
      hasDraggedRef.current = false;

      // Install document-level listeners. They will be removed on pointerup
      // and pointercancel. We pass through React refs so the up/cancel closure
      // can read the latest state.
      activeMoveHandler = (ev: PointerEvent) => {
        if (lastActivePointerId !== ev.pointerId) return;
        const moveOrigin = dragOriginRef.current;
        if (!moveOrigin) return;

        const moveDdx = ev.clientX - moveOrigin.x;
        const moveDdy = ev.clientY - moveOrigin.y;
        const moveBounds = getBounds();
        const moveClamped = Math.max(moveBounds.min, Math.min(moveBounds.max, startDxRef.current + moveDdx));
        pushDragTrace({
          event: 'pointermove', ts: Date.now(), pointerId: ev.pointerId,
          clientX: ev.clientX, clientY: ev.clientY,
          ddx: moveDdx, ddy: moveDdy, dragOrigin: moveOrigin,
          'hasDraggedRef.current': hasDraggedRef.current,
          isDragging, 'dx-after-clamp': moveClamped,
          viewport: typeof window === 'undefined' ? 0 : window.innerWidth,
          isFixed: typeof window !== 'undefined' && window.innerWidth <= 374,
          isMobile: isMobilePointer(),
          pointerType: ev.pointerType, threshold: getMoveThreshold(ev.pointerType),
          note: 'with-origin',
        });

        const lastX = lastMoveRef.current?.x ?? moveOrigin.x;
        const lastY = lastMoveRef.current?.y ?? moveOrigin.y;
        const incDdx = ev.clientX - lastX;
        const incDdy = ev.clientY - lastY;
        const incAbsDdx = Math.abs(incDdx);
        const incAbsDdy = Math.abs(incDdy);
        const cumulativeDdx = ev.clientX - moveOrigin.x;
        const cumulativeAbsDdx = Math.abs(cumulativeDdx);
        // [T-FLOATING-BAR-NAV-TOGGLE-HOLD-PY] Lock the gate to the press-time
        // pointer type. Real browsers (iOS Safari in particular) can change
        // `ev.pointerType` mid-stream when synthesizing mouse events from a
        // touch sequence; using a press-time snapshot guarantees the gate
        // decision is consistent for the lifetime of one gesture.
        const isTouchLike = touchIsTouchLikeRef.current;
        const threshold = getMoveThreshold(ev.pointerType);
        const vTolerance = hasDraggedRef.current ? V_GUARD_PRIMED_MULTIPLIER : V_GUARD_MULTIPLIER;
        lastMoveRef.current = { x: ev.clientX, y: ev.clientY };
        // Touch/pen sub-threshold jitter skip — only when cumulative threshold
        // not yet met AND not armed.
        if (isTouchLike && incAbsDdx < TOUCH_JITTER_PX && incAbsDdy < TOUCH_JITTER_Y_PX
            && cumulativeAbsDdx < threshold && !hasDraggedRef.current) return;
        // [T-FLOATING-BAR-NAV-TOGGLE-HOLD-PY] If a touch|pen pointer moves more than
        // TOUCH_HOLD_CANCEL_PX on either axis before the hold timer fires, the
        // user is swiping, not long-pressing. Clear the timer and refuse to arm.
        if (isTouchLike && !touchGateArmedRef.current) {
          if (Math.abs(ev.clientX - moveOrigin.x) > TOUCH_HOLD_CANCEL_PX
              || Math.abs(ev.clientY - moveOrigin.y) > TOUCH_HOLD_CANCEL_PX) {
            if (longPressTimerRef.current !== null) {
              clearTimeout(longPressTimerRef.current);
              longPressTimerRef.current = null;
            }
          }
          return;
        }
        // Touch/pen vertical-scroll guard: incremental Y vs 4x/5x tolerance.
        if (isTouchLike && incAbsDdy > vTolerance * Math.max(incAbsDdx, V_GUARD_MULTIPLIER)) return;
        const meetsThreshold = isTouchLike
          ? cumulativeAbsDdx >= threshold
          : cumulativeAbsDdx > threshold;
        if (!meetsThreshold && !hasDraggedRef.current) return;
        // [T-FLOATING-BAR-NAV-TOGGLE-HOLD-PY] Hold the line on touch|pen: if the
        // hold timer has not armed yet, refuse to translate the toggle. Returning
        // here keeps the gate a single source of truth — even when the pointer
        // drift is below TOUCH_HOLD_CANCEL_PX (no swipe-cancel triggered), there
        // must be no `setDx` and no `hasDraggedRef` flip until the user clearly
        // signals intent by holding in place for TOUCH_HOLD_GATE_MS.
        if (isTouchLike && !touchGateArmedRef.current) return;
        const next = startDxRef.current + cumulativeDdx;
        const { min, max } = getBounds();
        if (!isDragging) setIsDragging(true);
        hasDraggedRef.current = true;
        setDx(Math.max(min, Math.min(max, next)));
      };

      activeUpHandler = (ev: PointerEvent) => {
        if (lastActivePointerId !== ev.pointerId) return;
        // [T-FLOATING-BAR-NAV-TOGGLE-HOLD-PY] Lock the gate decision to the
        // press-time isTouchLike (NOT ev.pointerType) so mid-stream type
        // flips from synthesized events can't relax the gate at release.
        const isTouchLike = touchIsTouchLikeRef.current;
        pushDragTrace({
          event: 'pointerup', ts: Date.now(), pointerId: ev.pointerId,
          clientX: ev.clientX, clientY: ev.clientY, 'dx-before': dx, 'dx-final': dx,
          dragOrigin: dragOriginRef.current,
          'hasDraggedRef.current': hasDraggedRef.current,
          isFixed: typeof window !== 'undefined' && window.innerWidth <= 374,
          viewport: typeof window === 'undefined' ? 0 : window.innerWidth,
          isMobile: isMobilePointer(), stored: false, 'click-suppressed': false,
          pointerType: ev.pointerType, threshold: getMoveThreshold(ev.pointerType),
          note: 'pending',
        });
        touchGateArmedRef.current = false;
        if (longPressTimerRef.current !== null) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        // [T-FLOATING-BAR-NAV-TOGGLE-HOLD-PY] Touch|pen: clear the hold timer on release.
        if (isTouchLike && longPressTimerRef.current !== null) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        const gateArmed = touchGateArmedRef.current || !isTouchLike;
        if (hasDraggedRef.current && gateArmed) {
          const { min, max } = getBounds();
          let snapped = Math.max(min, Math.min(max, currentDxRef.current));
          const midpoint = (min + max) / 2;
          const snapPoints = [max, midpoint, min];
          let nearest = snapPoints[0];
          let nearestDist = Math.abs(snapped - nearest);
          for (let i = 1; i < snapPoints.length; i++) {
            const d = Math.abs(snapped - snapPoints[i]);
            if (d < nearestDist) { nearestDist = d; nearest = snapPoints[i]; }
          }
          if (nearestDist <= SNAP_THRESHOLD_PX) snapped = nearest;
          setDx(snapped);
          try {
            window.localStorage.setItem(DROPLET_X_KEY, String(snapped));
          } catch { /* ignore */ }
        }
        setIsDragging(false);
        dragOriginRef.current = null;
        lastMoveRef.current = null;
        teardownDocumentHandlers();
      };

      activeCancelHandler = (ev: PointerEvent) => {
        if (lastActivePointerId !== ev.pointerId) return;
        pushDragTrace({
          event: 'pointercancel', ts: Date.now(), pointerId: ev.pointerId,
          clientX: ev.clientX, clientY: ev.clientY,
          dragOrigin: dragOriginRef.current,
          'hasDraggedRef.current': hasDraggedRef.current,
          isFixed: typeof window !== 'undefined' && window.innerWidth <= 374,
          viewport: typeof window === 'undefined' ? 0 : window.innerWidth,
          isMobile: isMobilePointer(), 'click-suppressed': hasDraggedRef.current,
          pointerType: ev.pointerType, threshold: getMoveThreshold(ev.pointerType),
          note: 'cancel',
        });
        touchGateArmedRef.current = false;
        if (longPressTimerRef.current !== null) {
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
        }
        setIsDragging(false);
        dragOriginRef.current = null;
        lastMoveRef.current = null;
        teardownDocumentHandlers();
      };

      document.addEventListener('pointermove', activeMoveHandler);
      document.addEventListener('pointerup', activeUpHandler);
      document.addEventListener('pointercancel', activeCancelHandler);
    },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- setDx is a stable setter; intentionally omitted to keep listener registration stable
    [dx, isDragging, getBounds],
  );

  // Click-vs-drag guard: call from onClick; returns true if this click should
  // be treated as a drag-committed gesture and thus suppressed.
  const consumeHasDragged = useCallback((): boolean => {
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    const clampToViewport = () => {
      const bounds = getBounds();
      setDxState((current) => clampToBounds(current, bounds));
      if (typeof window !== 'undefined') {
        const nextFixed = window.innerWidth <= 374;
        setIsFixedFallback((prev) => (prev === nextFixed ? prev : nextFixed));
      }
    };

    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    // Initialize on first mount so a stale ref from an unrelated component is
    // never reused at the first pointerdown (defensive; the press handler always
    // overwrites this value with the correct press-time snapshot).
    touchIsTouchLikeRef.current = false;
    return () => {
      window.removeEventListener('resize', clampToViewport);
      teardownDocumentHandlers();
      if (longPressTimerRef.current !== null) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
  }, [getBounds, setDxState]);

  return {
    dx,
    isDragging,
    isFixedFallback,
    consumeHasDragged,
    handlePointerDown,
  };
}
